import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

const VUEX_PROPERTIES = ['state', 'getters', 'actions', 'mutations']

let store = {};

(function updateModules () {
  store = normalizeRoot(require('../store/index.js'), 'store/index.js')

  // If store is an exported method = classic mode (deprecated)

  if (typeof store === 'function') {
    return console.warn('Classic mode for store/ is deprecated and will be removed in Nuxt 3.')
  }

  // Enforce store modules
  store.modules = store.modules || {}

  resolveStoreModules(require('../store/modules/analysis.js'), 'modules/analysis.js')
  resolveStoreModules(require('../store/modules/auth.js'), 'modules/auth.js')
  resolveStoreModules(require('../store/modules/bookmarks.js'), 'modules/bookmarks.js')
  resolveStoreModules(require('../store/modules/dialog.js'), 'modules/dialog.js')
  resolveStoreModules(require('../store/modules/feedFilters.js'), 'modules/feedFilters.js')
  resolveStoreModules(require('../store/modules/global.js'), 'modules/global.js')
  resolveStoreModules(require('../store/modules/guide.js'), 'modules/guide.js')
  resolveStoreModules(require('../store/modules/hots.js'), 'modules/hots.js')
  resolveStoreModules(require('../store/modules/inFocus.js'), 'modules/inFocus.js')
  resolveStoreModules(require('../store/modules/insights.js'), 'modules/insights.js')
  resolveStoreModules(require('../store/modules/jurisdiction.js'), 'modules/jurisdiction.js')
  resolveStoreModules(require('../store/modules/main.js'), 'modules/main.js')
  resolveStoreModules(require('../store/modules/pages.js'), 'modules/pages.js')
  resolveStoreModules(require('../store/modules/pageView.js'), 'modules/pageView.js')
  resolveStoreModules(require('../store/modules/profile.js'), 'modules/profile.js')
  resolveStoreModules(require('../store/modules/reportBuilder.js'), 'modules/reportBuilder.js')
  resolveStoreModules(require('../store/modules/requirement.js'), 'modules/requirement.js')
  resolveStoreModules(require('../store/modules/search.js'), 'modules/search.js')
  resolveStoreModules(require('../store/modules/tracker.js'), 'modules/tracker.js')
  resolveStoreModules(require('../store/modules/trackers.js'), 'modules/trackers.js')
  resolveStoreModules(require('../store/modules/updates.js'), 'modules/updates.js')
  resolveStoreModules(require('../store/modules/verticals.js'), 'modules/verticals.js')
  resolveStoreModules(require('../store/modules/viq.js'), 'modules/viq.js')
  resolveStoreModules(require('../store/modules/workspace.js'), 'modules/workspace.js')

  // If the environment supports hot reloading...

  if (process.client && module.hot) {
    // Whenever any Vuex module is updated...
    module.hot.accept([
      '../store/index.js',
      '../store/modules/analysis.js',
      '../store/modules/auth.js',
      '../store/modules/bookmarks.js',
      '../store/modules/dialog.js',
      '../store/modules/feedFilters.js',
      '../store/modules/global.js',
      '../store/modules/guide.js',
      '../store/modules/hots.js',
      '../store/modules/inFocus.js',
      '../store/modules/insights.js',
      '../store/modules/jurisdiction.js',
      '../store/modules/main.js',
      '../store/modules/pages.js',
      '../store/modules/pageView.js',
      '../store/modules/profile.js',
      '../store/modules/reportBuilder.js',
      '../store/modules/requirement.js',
      '../store/modules/search.js',
      '../store/modules/tracker.js',
      '../store/modules/trackers.js',
      '../store/modules/updates.js',
      '../store/modules/verticals.js',
      '../store/modules/viq.js',
      '../store/modules/workspace.js',
    ], () => {
      // Update `root.modules` with the latest definitions.
      updateModules()
      // Trigger a hot update in the store.
      window.$nuxt.$store.hotUpdate(store)
    })
  }
})()

// createStore
export const createStore = store instanceof Function ? store : () => {
  return new Vuex.Store(Object.assign({
    strict: (process.env.NODE_ENV !== 'production')
  }, store))
}

function normalizeRoot (moduleData, filePath) {
  moduleData = moduleData.default || moduleData

  if (moduleData.commit) {
    throw new Error(`[nuxt] ${filePath} should export a method that returns a Vuex instance.`)
  }

  if (typeof moduleData !== 'function') {
    // Avoid TypeError: setting a property that has only a getter when overwriting top level keys
    moduleData = Object.assign({}, moduleData)
  }
  return normalizeModule(moduleData, filePath)
}

function normalizeModule (moduleData, filePath) {
  if (moduleData.state && typeof moduleData.state !== 'function') {
    console.warn(`'state' should be a method that returns an object in ${filePath}`)

    const state = Object.assign({}, moduleData.state)
    // Avoid TypeError: setting a property that has only a getter when overwriting top level keys
    moduleData = Object.assign({}, moduleData, { state: () => state })
  }
  return moduleData
}

function resolveStoreModules (moduleData, filename) {
  moduleData = moduleData.default || moduleData
  // Remove store src + extension (./foo/index.js -> foo/index)
  const namespace = filename.replace(/\.(js|mjs)$/, '')
  const namespaces = namespace.split('/')
  let moduleName = namespaces[namespaces.length - 1]
  const filePath = `store/${filename}`

  moduleData = moduleName === 'state'
    ? normalizeState(moduleData, filePath)
    : normalizeModule(moduleData, filePath)

  // If src is a known Vuex property
  if (VUEX_PROPERTIES.includes(moduleName)) {
    const property = moduleName
    const propertyStoreModule = getStoreModule(store, namespaces, { isProperty: true })

    // Replace state since it's a function
    mergeProperty(propertyStoreModule, moduleData, property)
    return
  }

  // If file is foo/index.js, it should be saved as foo
  const isIndexModule = (moduleName === 'index')
  if (isIndexModule) {
    namespaces.pop()
    moduleName = namespaces[namespaces.length - 1]
  }

  const storeModule = getStoreModule(store, namespaces)

  for (const property of VUEX_PROPERTIES) {
    mergeProperty(storeModule, moduleData[property], property)
  }

  if (moduleData.namespaced === false) {
    delete storeModule.namespaced
  }
}

function normalizeState (moduleData, filePath) {
  if (typeof moduleData !== 'function') {
    console.warn(`${filePath} should export a method that returns an object`)
    const state = Object.assign({}, moduleData)
    return () => state
  }
  return normalizeModule(moduleData, filePath)
}

function getStoreModule (storeModule, namespaces, { isProperty = false } = {}) {
  // If ./mutations.js
  if (!namespaces.length || (isProperty && namespaces.length === 1)) {
    return storeModule
  }

  const namespace = namespaces.shift()

  storeModule.modules[namespace] = storeModule.modules[namespace] || {}
  storeModule.modules[namespace].namespaced = true
  storeModule.modules[namespace].modules = storeModule.modules[namespace].modules || {}

  return getStoreModule(storeModule.modules[namespace], namespaces, { isProperty })
}

function mergeProperty (storeModule, moduleData, property) {
  if (!moduleData) {
    return
  }

  if (property === 'state') {
    storeModule.state = moduleData || storeModule.state
  } else {
    storeModule[property] = Object.assign({}, storeModule[property], moduleData)
  }
}
